<template>
    <div class="plottable-chart stacked-bar-tier">
        <loader class="panel-loader" v-if="!isDataLoaded"></loader>
        <div class="chart-container" v-if="isDataLoaded && !hasError">
            <div class="chart-controls">
                <div class="breadcrumb-container">
                    <ul class="breadcrumb" v-if="dataZoom.length">
                        <li v-on:click="dataZoomReset()" class="clickable">Top</li>
                        <li v-for="(zoom,index) in dataZoom" v-bind:class="breadcrumbClickable(index)" v-on:click="breadcrumbZoomOut(index)">{{parentLabel(zoom)}}</li>
                    </ul>
                </div>
                <div class="filter" v-if="hasFilter">
                    <template v-for="(filterValue, filterName) in filter">
                        <select class="form-control" v-model="filter[filterName]" v-on:change="getData()">
                            <option v-for="filterOption in filterOptions[filterName]" v-bind:value="filterOption.value">{{filterOption.label}}</option>
                        </select>
                    </template>
                </div>
                <div class="table-toggle clickable glyphicon" v-bind:class="tableView ? 'glyphicon-signal' : 'glyphicon-th-list'" v-if="hasTable" v-on:click="toggleTable()" />
            </div>
            <div class="chart" v-show="!showTable"></div>
            <div class="table-data" v-show="showTable">
                <table class="table">
                    <thead><tr><th v-for="field in content['tableMetaData']">{{field.label || field}}</th></tr></thead>
                    <tbody><tr v-for="row in filteredTableData()" v-bind:class="tableClickable(row['id'])" v-on:click="tableZoom (row['id'])" ><td v-for="field in trimmedRowData(row)">{{field}}</td></tr></tbody>
                </table>
            </div>
        </div>
        <div class="error-container alert alert-danger" v-if="isDataLoaded && hasError">
            {{error}}
        </div>
        <div class="chart-generation-date" v-if="isDataLoaded && hasGenDate">
            Data generated at: {{content['genDate']}}
        </div>
    </div>
</template>

<script>
    import * as d3 from 'd3'
    import * as Plottable from 'plottable/build/src'
    import axios from 'axios'
    import querystring from 'querystring'
    import Loader from '@/components/Loader'
    import ChartMixin from '@/mixins/ChartMixin.js'
    import hrNumber from '@/plugins/HumanReadableNumbers'

    export default {
        props: ['contentSource', 'id', 'colorSet'],
        mixins: [ChartMixin],
        name: 'stackedBarTier',
        data () {
            return {
                filter: null,
                filterOptions: [],
                content: null,
                components: {},
                dataSets: {},
                dataZoom: [],
                tableView: false
            }
        },
        computed: {
            hasFilter () {
                return this.filter !== null
            },
            hasGenDate () {
                return this.content.genDate !== undefined && this.content.genDate !== null
            },
            hasTable () {
                return this.content.tableData !== undefined && this.content.tableData !== null
            },
            showTable () {
                return this.hasTable && this.tableView
            },
            isDataLoaded () {
                return this.content !== null
            },
            hasError () {
                return this.content.error !== undefined && this.content.error !== null
            },
            error () {
                return this.content.error || 'An unknown error has occurred'
            },
            parent () {
                return this.dataZoom[this.dataZoom.length - 1] || 0
            }
        },
        methods: {
            breadcrumbClickable (index) {
                return this.dataZoom.length - 1 > index ? 'clickable' : ''
            },
            tableClickable (id) {
                return this.hasChildren(id, this.content.data) ? 'clickable' : ''
            },
            toggleTable () {
                this.tableView = !this.tableView

                this.$nextTick(function () {
                    this.drawChart()
                })
            },
            _scales () {
                this.components.scales = this.components.scales || {
                    'x': new Plottable.Scales.Linear().domainMin(0),
                    'y': new Plottable.Scales.Category(),
                    'color': new Plottable.Scales.Color().range(this.colorSet || [
                        '#023b6c',
                        '#66ca38',
                        '#09caf3',
                        '#7b1584',
                        '#ffde00',
                        '#ffa200',
                        '#545554'
                    ])
                }

                return this.components.scales
            },
            _xLabel () {
                return this.components.xLabel || null
            },
            _legend () {
                const self = this
                this.components.legend = this.components.legend || new Plottable.Components.Legend(this._scales().color).symbol(Plottable.SymbolFactories.square).symbolOpacity(function (label) {
                    for (let set in self.content.setMetaData) {
                        if (self.content.setMetaData[set].label === label) {
                            return self.content.setMetaData[set].active ? 1 : 0.25
                        }
                    }
                })

                return this.components.legend
            },
            _axes () {
                this.components.axes = this.components.axes || {
                    'x': new Plottable.Axes.Numeric(this._scales().x, 'bottom').formatter(function (d) { return hrNumber(d, 5, 2) }),
                    'y': new Plottable.Axes.Category(this._scales().y, 'left')
                }

                return this.components.axes
            },
            _plot () {
                const self = this
                this.components.plot = this.components.plot || new Plottable.Plots.StackedBar('horizontal')
                    .x(function (d) { return d.value }, this._scales().x)
                    .y(function (d) { return d.label }, this._scales().y)
                    .attr('fill', function (d, i, dataset) { return dataset.metadata() }, this._scales().color)
                    .attr('class', function (d) { return self.hasChildren(d.id, self.content.data) ? 'clickable' : '' })
                    .animated(true)

                return this.components.plot
            },
            _chart () {
                this.components.chart = this.components.chart || new Plottable.Components.Table([
                    [this._axes().y, this._plot(), this._legend()],
                    [null, this._axes().x, null],
                    [null, this._xLabel(), null]
                ])

                return this.components.chart
            },
            _interactions () {
                this.components.interactions = this.components.interactions || {
                    legend: new Plottable.Interactions.Click(),
                    plotZoom: new Plottable.Interactions.Click(),
                    pointer: new Plottable.Interactions.Pointer(),
                    panZoom: new Plottable.Interactions.PanZoom().addYScale(this._scales().y),
                    panZoomAxis: new Plottable.Interactions.PanZoom().addYScale(this._scales().y)
                }

                return this.components.interactions
            },
            parentLabel (parent) {
                const data = this.content.data.filter((data) => data.id === parent)
                return data.length ? data[0].label || '' : ''
            },
            legendFilter (point) {
                const label = this._legend().entitiesAt(point)[0].selection

                for (let set in this.content.setMetaData) {
                    if (this.content.setMetaData[set].label === label.select('title').html()) {
                        this.content.setMetaData[set].active = !this.content.setMetaData[set].active
                    }
                }

                this.redrawChart()
            },
            attachLegendFilter () {
                this._interactions().legend.onClick(this.legendFilter).attachTo(this._legend())
            },
            attachPanZoomControls () {
                this._interactions().panZoom.setMinMaxDomainValuesTo(this._scales().y)
                this._interactions().panZoom.attachTo(this._plot())
                this._interactions().panZoomAxis.setMinMaxDomainValuesTo(this._scales().y)
                this._interactions().panZoomAxis.attachTo(this._axes().y)
            },
            plotZoom (point) {
                const element = this._plot().entitiesAt(point)[0]

                if (element) {
                    const id = element.selection.data()[0].id
                    if (this.hasChildren(id, this.content.data)) {
                        this.dataZoomIn(this, element.selection.data()[0].id)
                    }
                    return
                }

                this.dataZoomOut(this)
            },
            attachPlotZoomControls () {
                this._interactions().plotZoom.onClick(this.plotZoom).attachTo(this._plot())
            },
            tooltipShow (point) {
                var element = this._plot().entitiesAt(point)[0]
                var tooltipAnchor = this.components.toolTipAnchor
                if (element) {
                    tooltipAnchor.attrs({
                        x: point.x,
                        y: point.y,
                        opacity: 1
                    })
                    this.components.tooltip.classed('active', true)
                    this.components.tooltip.html(this.buildToolTip(element.datum))
                } else {
                    tooltipAnchor.attrs({
                        opacity: 0
                    })
                    this.components.tooltip.classed('active', false)
                }
            },
            tooltipHide () {
                this.components.toolTipAnchor.attrs({
                    opacity: 0
                })
                this.components.tooltip.classed('active', false)
            },
            buildTooltipContainer () {
                if (this.components.toolTipAnchor !== undefined && this.components.tooltip !== undefined) {
                    return
                }

                this.components.toolTipAnchor = this.components.toolTipAnchor || this._plot().foreground().append('foreignObject')

                this.components.toolTipAnchor.attrs({
                    opacity: 1,
                    height: '1px',
                    width: '1px',
                    x: 0,
                    y: 0
                })

                this.components.tooltip = this.components.tooltip || this.components.toolTipAnchor.append('xhtml:div')

                this.components.tooltip.attrs({
                    xmlns: 'http://www.w3.org/1999/xhtml',
                    x: 0,
                    y: 0,
                    class: 'tooltip plottable-tooltip'
                })

                this._interactions().pointer
                    .onPointerMove(this.tooltipShow)
                    .onPointerExit(this.tooltipHide)
                    .attachTo(this._plot())
            },
            buildToolTip (data) {
                let tooltip = ''

                const metadata = this.content.setMetaData[data.type] || {}

                for (let tooltipLine of metadata.tooltip || []) {
                    let field = tooltipLine.label || ''
                    let value = this.formatValue(data, tooltipLine.values)

                    if (field !== '' && value !== '') {
                        tooltip = tooltip + '<tr><td>' + field + '</td><td>' + value + '</td></tr>'
                    }
                }

                return tooltip !== '' ? '<table>' + tooltip + '</table>' : ''
            },
            getData () {
                this.content = null
                let self = this
                return axios.post(this.contentSource, querystring.stringify(Object.assign({id: this.id}, this.filter)), {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
                    .then(function (response) {
                        if (response.data.result) {
                            self.setData(self, response.data.content)
                            self.$nextTick(function () {
                                self.drawChart()
                            })
                        } else {
                            self.setData(self, {error: response.data.message || 'An unknown error has occurred'})
                        }
                    })
                    .catch((error) => {
                        if (error instanceof Error) {
                            self.setData(self, {error: error.message || 'An unknown error has occurred'})
                        }
                    })
            },
            drawChart () {
                this.destroyChart()
                this.attachLegendFilter()
                this.attachPlotZoomControls()
                this.attachPanZoomControls()
                this.components.xLabel = new Plottable.Components.AxisLabel(this.content['xLabel'] || null)

                this.updateDataSets()

                for (let dataSet in this.dataSets) {
                    this._plot().addDataset(this.dataSets[dataSet])
                }

                const chartContainer = d3.select(this.$el.querySelector('.chart'))

                this._chart().renderTo(chartContainer)

                this.buildTooltipContainer()

                const chart = this._chart()

                window.addEventListener('resize', function () {
                    chart.redraw()
                })
            },
            redrawChart () {
                this.updateDataSets()
            },
            destroyChart () {
                this.destroyComponents(this.components)
                this.$forceUpdate()
            },
            updateDataSets () {
                const self = this
                for (let set in this.$data.content.setMetaData) {
                    let dataFilter = function (data) {
                        return self.content.setMetaData[set].active &&
                            data.parent === self.parent &&
                            data['type'] === set
                    }

                    if (this.content.setMetaData[set].active === undefined) {
                        this.content.setMetaData[set].active = true
                    }

                    if (this.dataSets[set] === undefined) {
                        let data = this.content.data.filter(dataFilter)
                        this.dataSets[set] = new Plottable
                            .Dataset(data.length ? data : this.getEmptyDataSet())
                            .metadata(this.content.setMetaData[set].label || set)
                    } else {
                        let data = this.content.data.filter(dataFilter)
                        this.dataSets[set].data(data.length ? data : this.getEmptyDataSet())
                    }
                }
            },
            getEmptyDataSet () {
                const labels = [...new Set(this.content.data
                    .filter((data) => data.parent === this.parent)
                    .map(item => item.label))]
                const emptySet = []

                labels.forEach(label => {
                    emptySet[emptySet.length] = {
                        label: label
                    }
                })

                return emptySet
            },
            dataZoomReset () {
                this.dataZoom = []
                this.redrawChart()
            },
            breadcrumbZoomOut (index) {
                if (index + 1 < this.dataZoom.length) {
                    this.dataZoomOut(this, this.dataZoom[index + 1])
                }
            },
            tableZoom (id) {
                if (this.hasChildren(id, this.content.data)) {
                    this.dataZoomIn(this, id)
                } else {
                    this.dataZoomOut(this)
                }
            },
            filteredTableData () {
                if (this.hasTable) {
                    const self = this

                    return this.content.tableData.filter(data => {
                        return data.parent === self.parent
                    })
                }
            },
            trimmedRowData (row) {
                return this.trimRow(row, this.content.tableMetaData)
            }
        },
        created () {
            if (this.contentSource !== undefined && this.contentSource !== null) {
                this.getData()
            }
        },
        components: { Loader }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>

</style>



// WEBPACK FOOTER //
// StackedBarTier.vue?2aa33b75